import { linkToFormSubmissionDetail } from "#src/Routers/links";
import {
  FormSubmissionsTableFilterArea,
  FormSubmissionsTableFilterAreaDrawerContent,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { getStatusType } from "#src/components/Common/Table/rendererHelper";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { FilterArea } from "#src/components/FilterArea";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { UseQueryOptions, useQueries, useQuery } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  Pill,
  StorageKeys,
  TextInput,
} from "@validereinc/common-components";
import {
  Resources,
  SortDirection,
  UserReturnType,
  UsersAdapter,
  WorkflowAdapter,
  WorkflowFormSubmissionType,
  type FormSubmissionGetListRequestType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo } from "react";

export const WorkflowFormSubmissionsTablePanel = ({
  workflowId,
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: {
  workflowId: string;
} & Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const [filters] = useStickyState<FormSubmissionGetListRequestType["filters"]>(
    {},
    filterConfigStorageKey
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    undefined,
    tableStateStorageKey
  );

  const requestPayload: Parameters<typeof WorkflowAdapter.forms.getList>[0] = {
    workflowId,
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    // @ts-expect-error TODO: Typing on filter
    filters: filters,
  };

  const query = useQuery({
    queryKey: [Resources.WORKFLOW, "forms", requestPayload],
    queryFn: async () => {
      const { data, total_entries } =
        await WorkflowAdapter.forms.getList(requestPayload);

      updateTableState({ total: total_entries });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const formSubmissionsCreatedByUsers = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries:
      query?.data?.map((submission: WorkflowFormSubmissionType) => ({
        queryKey: ["users", submission.created_by],
        queryFn: () =>
          UsersAdapter.getOne({
            id: submission.created_by,
          }),
        enabled: Boolean(submission.created_by),
        staleTime: 3 * 60 * 1000,
        select: (resp) => resp?.data,
      })) ?? [],
  });

  const formSubmissionsCreatedByUsersMap = useMemo(() => {
    return formSubmissionsCreatedByUsers.reduce<Record<string, UserReturnType>>(
      (map, q) => {
        if (!q.data?.id || map[q.data.id]) {
          return map;
        }

        map[q.data.id] = q.data;
        return map;
      },
      {}
    );
  }, [formSubmissionsCreatedByUsers]);

  const submissionsHeaders: Array<HeaderType<WorkflowFormSubmissionType>> = [
    {
      label: "Name",
      key: "id",
      isSortable: false,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFormSubmissionDetail(item.id)}>
          {`${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`}
        </RoutingLink>
      ),
    },
    {
      label: "Template",
      key: "form_template.name",
      isSortable: false,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={linkToFormTemplateDetail(
            item.form_schema?.form_category?.id,
            item.form_schema?.id
          )}
        >
          {item?.form_schema?.name}
        </RoutingLink>
      ),
    },
    {
      label: "Category",
      key: "form_template.form_category.name",
      isSortable: false,
      renderComponent: ({ item }: { item: WorkflowFormSubmissionType }) => (
        <RoutingLink
          to={linkToFormCategoryDetails(item.form_schema?.form_category?.id)}
        >
          {item?.form_schema?.form_category?.name}
        </RoutingLink>
      ),
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }) => (
        <Pill variant={getStatusType(item.status, "form").type}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Associated Task",
      key: "task",
      isSortable: false,
      renderComponent: ({ item }) => {
        if (item?.is_workflow_trigger) {
          return "Workflow Trigger";
        } else if (item?.associated_tasks?.length) {
          return item?.associated_tasks?.[0]?.workflow_task_name;
        } else if (item?.associated_actions?.length) {
          return item?.associated_actions?.[0]?.workflow_action_name;
        } else {
          return "-";
        }
      },
    },
    {
      label: "Submission Date",
      key: "updated_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.updated_at}
          withTime={false}
        />
      ),
    },
    {
      label: "Submitted By",
      key: "created_by",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.created_by ? (
          <UserDataDisplay
            id={item.created_by}
            name={
              formSubmissionsCreatedByUsersMap[item.created_by]?.name ?? "n/a"
            }
          />
        ) : (
          "-"
        ),
    },
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      filterComponent={
        <FormSubmissionsTableFilterArea
          filterConfigStorageKey={filterConfigStorageKey}
          filterContentSlot={
            <FilterArea.Content>
              {({ handleOnChange }) => (
                <div style={{ marginRight: 8 }}>
                  <TextInput
                    name="form_schema.name"
                    label="Search"
                    type="search"
                    placeholder="Search Submissions..."
                    isLabelShown={false}
                    onChange={(val) => handleOnChange(val, "form_schema.name")}
                    isInline
                  />
                </div>
              )}
            </FilterArea.Content>
          }
          filterDrawerContentSlot={
            <FormSubmissionsTableFilterAreaDrawerContent
              hasCategoryFilter
              hasTemplateFilter
              hasStatusFilter
            />
          }
        />
      }
      panelProps={{
        title: "Form Submissions",
      }}
      dataTableProps={{
        headers: submissionsHeaders,
        isLoading: query?.isLoading,
        items: query?.data ?? [],
        getItemId: (item) => item.id,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: tableState.total,
        },
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
    />
  );
};
